import { ReportData, Question } from './types';
import { Timestamp } from 'firebase/firestore';
import { VISUALIZATION_TYPES } from './constants';

export function transformReportData(rawData: any): ReportData {
  // Ensure all required top-level properties exist
  if (!rawData.questions || !rawData.feedbackComments || !rawData.metadata) {
    throw new Error('Missing required report data properties');
  }

  return {
    questions: transformQuestions(rawData.questions),
    feedbackComments: transformFeedbackComments(rawData.feedbackComments),
    discResults: rawData.discResults,
    metadata: transformMetadata(rawData.metadata)
  };
}

function transformQuestions(rawQuestions: any[]): Question[] {
  if (!Array.isArray(rawQuestions)) {
    throw new Error('Questions must be an array');
  }

  return rawQuestions.map((q, index) => ({
    id: ensureNumber(q.id, `Question ${index}: Invalid id`),
    text: ensureString(q.text, `Question ${index}: Invalid text`),
    selfAssessment: ensureNumber(q.selfAssessment, `Question ${index}: Invalid self assessment`),
    evaluators: ensureNumberArray(q.evaluators, `Question ${index}: Invalid evaluators`),
    visualization: ensureVisualization(q.visualization, `Question ${index}: Invalid visualization`)
  }));
}

function transformFeedbackComments(rawComments: any): Record<number, string[]> {
  const transformed: Record<number, string[]> = {};
  
  Object.entries(rawComments).forEach(([key, value]) => {
    const questionId = Number(key);
    if (isNaN(questionId)) {
      throw new Error(`Invalid feedback comment key: ${key}`);
    }

    if (!Array.isArray(value)) {
      throw new Error(`Feedback comments for question ${key} must be an array`);
    }

    transformed[questionId] = (value as any[]).map(comment => 
      ensureString(comment, `Invalid feedback comment for question ${key}`)
    );
  });
  
  return transformed;
}

function transformMetadata(rawMetadata: any) {
  // Convert string timestamps back to Firestore Timestamps
  const generatedAt = rawMetadata.generatedAt?.seconds ? 
    new Timestamp(rawMetadata.generatedAt.seconds, rawMetadata.generatedAt.nanoseconds) :
    Timestamp.now();

  const updatedAt = rawMetadata.updatedAt?.seconds ?
    new Timestamp(rawMetadata.updatedAt.seconds, rawMetadata.updatedAt.nanoseconds) :
    Timestamp.now();

  return {
    generatedAt,
    updatedAt,
    status: ensureStatus(rawMetadata.status),
    version: ensureString(rawMetadata.version, 'Invalid version')
  };
}

// Helper functions
function ensureNumber(value: any, errorMessage: string): number {
  const num = Number(value);
  if (isNaN(num)) {
    throw new Error(errorMessage);
  }
  return num;
}

function ensureString(value: any, errorMessage: string): string {
  if (typeof value !== 'string' || !value.trim()) {
    throw new Error(errorMessage);
  }
  return value.trim();
}

function ensureNumberArray(value: any, errorMessage: string): number[] {
  if (!Array.isArray(value)) {
    throw new Error(errorMessage);
  }
  return value.map(v => ensureNumber(v, errorMessage));
}

function ensureVisualization(value: any, errorMessage: string): Question['visualization'] {
  if (!value || !VISUALIZATION_TYPES.includes(value)) {
    throw new Error(`${errorMessage}. Must be one of: ${VISUALIZATION_TYPES.join(', ')}`);
  }
  return value;
}

function ensureStatus(value: any): 'pending' | 'completed' {
  if (value !== 'pending' && value !== 'completed') {
    throw new Error('Invalid status value');
  }
  return value;
}